import { Alert, Box, Button, Card, PageHeader, Text, TextInput, Textarea } from '@tocktix/pantry';
import {
  VenueMigrationStatusQuery,
  useContinueVenueMigrationMutation,
  useRetryVenueMigrationMutation,
  useScheduleVenueMigrationMutation,
  useVenueMigrationStatusQuery,
} from 'generated/graphql';
import { gql } from '@apollo/client';
import React, { FormEvent, useEffect, useState } from 'react';

// EXPERIMENTAL: This UI operates a mock-data POC and must not be used in production.
gql`
  query VenueMigrationStatus($venueId: Int!) {
    venueMigration(venueId: $venueId) {
      workflowIdentifier {
        workflowId
        runId
      }
      state
      canContinue
      canRetry
      failureReason
      progress {
        venueId
        scheduledMigrationTime
        stage
        migrationAttempt
        issues {
          type
          message
        }
      }
    }
  }

  mutation ScheduleVenueMigration($venueId: Int!, $scheduledMigrationTime: Instant!) {
    scheduleVenueMigration(venueId: $venueId, scheduledMigrationTime: $scheduledMigrationTime) {
      workflowId
      runId
    }
  }

  mutation ContinueVenueMigration($venueId: Int!) {
    continueVenueMigration(venueId: $venueId) {
      workflowIdentifier {
        workflowId
        runId
      }
      state
      canContinue
      canRetry
      failureReason
      progress {
        venueId
        scheduledMigrationTime
        stage
        migrationAttempt
        issues {
          type
          message
        }
      }
    }
  }

  mutation RetryVenueMigration($venueId: Int!) {
    retryVenueMigration(venueId: $venueId) {
      workflowIdentifier {
        workflowId
        runId
      }
      state
      canContinue
      canRetry
      failureReason
      progress {
        venueId
        scheduledMigrationTime
        stage
        migrationAttempt
        issues {
          type
          message
        }
      }
    }
  }
`;

const STATUS_POLL_INTERVAL_MS = 5000;
const MAX_VENUE_IDS = 100;

const stageLabels = {
  PREPARING: 'Running initial setup',
  WAITING_FOR_SCHEDULED_TIME: 'Waiting for the scheduled migration time',
  MIGRATING: 'Running the migration child workflow',
  WAITING_FOR_EXCEPTION_RESOLUTION: 'Waiting for migration issues to be resolved',
  WAITING_FOR_MANUAL_CONTINUATION: 'Waiting for manual continuation (legacy)',
  FINALIZING: 'Running finalization',
  COMPLETED: 'Migration completed',
} as const;

const issueLabels = {
  NEW_SLOT_CREATED: 'New slot created',
  TABLE_ASSIGNMENT_MISSING: 'Table assignment missing',
  TABLE_ASSIGNMENT_AMBIGUOUS: 'Table assignment ambiguous',
  RESERVATION_NOTE_TRUNCATED: 'Reservation note truncated',
  GUEST_NOTE_TRUNCATED: 'Guest note truncated',
  DUPLICATE_SOURCE_MARKERS: 'Duplicate source markers found',
  UNMATCHED_GUEST_PRUNED: 'Unmatched guest skipped',
  IMPORT_REINDEXING_FAILED: 'Import reindexing failed',
} as const;

type Migration = NonNullable<VenueMigrationStatusQuery['venueMigration']>;

type VenueIdParseResult = { venueIds: number[]; error: null } | { venueIds: null; error: string };

const presentMigration = (
  migration: Migration
): { color: 'primary' | 'success' | 'warning' | 'danger'; title: string; message: string } => {
  const stage = migration.progress?.stage;
  switch (migration.state) {
    case 'RUNNING':
      return {
        color: migration.canContinue ? 'warning' : 'primary',
        title: migration.canContinue ? 'Operator action required' : 'Migration is running',
        message: migration.canContinue
          ? 'Review the migration issue below, correct any source data if needed, then confirm or retry.'
          : stage
            ? stageLabels[stage]
            : 'Temporal has not reported workflow progress yet.',
      };
    case 'COMPLETED':
      return {
        color: 'success',
        title: 'Migration completed',
        message: 'All migration steps finished successfully.',
      };
    case 'FAILED':
      return {
        color: 'danger',
        title: 'Migration failed',
        message: migration.failureReason ?? 'Temporal did not return a failure reason.',
      };
    case 'TIMED_OUT':
      return {
        color: 'danger',
        title: 'Migration timed out',
        message: migration.failureReason ?? 'The workflow did not finish before its timeout.',
      };
    case 'CANCELED':
    default:
      return {
        color: 'warning',
        title: 'Migration canceled',
        message: migration.failureReason ?? 'The workflow was stopped before it completed.',
      };
  }
};

const parseVenueIds = (value: string): VenueIdParseResult => {
  const inputs = value
    .split(/[\s,]+/)
    .map((input) => input.trim())
    .filter(Boolean);
  if (inputs.length === 0) {
    return { venueIds: null, error: 'Enter at least one venue id.' };
  }

  const invalidInputs = inputs.filter((input) => {
    const venueId = Number(input);
    return !Number.isInteger(venueId) || venueId <= 0 || venueId > 2_147_483_647;
  });
  if (invalidInputs.length > 0) {
    return {
      venueIds: null,
      error: `Venue ids must be positive whole numbers. Invalid: ${invalidInputs.join(', ')}.`,
    };
  }

  const venueIds = [...new Set(inputs.map(Number))];
  if (venueIds.length > MAX_VENUE_IDS) {
    return {
      venueIds: null,
      error: `Enter no more than ${MAX_VENUE_IDS} venue ids at a time.`,
    };
  }
  return { venueIds, error: null };
};

const formatTimestamp = (value: string): string =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'long',
  }).format(new Date(value));

interface VenueMigrationCardProps {
  venueId: number;
}

const VenueMigrationCard: React.FC<VenueMigrationCardProps> = ({ venueId }) => {
  const [actionError, setActionError] = useState<string | null>(null);
  const [continueVenueMigration, { loading: isContinuing }] = useContinueVenueMigrationMutation();
  const [retryVenueMigration, { loading: isRetrying }] = useRetryVenueMigrationMutation();
  const {
    data,
    error: statusError,
    loading: isLoadingStatus,
    refetch,
    startPolling,
    stopPolling,
  } = useVenueMigrationStatusQuery({
    variables: { venueId },
    fetchPolicy: 'network-only',
  });
  const migration = data?.venueMigration ?? null;

  useEffect(() => {
    if (migration && migration.state !== 'RUNNING') {
      stopPolling();
      return;
    }
    startPolling(STATUS_POLL_INTERVAL_MS);
    return stopPolling;
  }, [migration, startPolling, stopPolling]);

  const handleContinue = async () => {
    setActionError(null);
    try {
      await continueVenueMigration({ variables: { venueId } });
      await refetch();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : String(reason));
    }
  };

  const handleRetry = async () => {
    setActionError(null);
    try {
      await retryVenueMigration({ variables: { venueId } });
      await refetch();
    } catch (reason) {
      setActionError(reason instanceof Error ? reason.message : String(reason));
    }
  };

  return (
    <Card data-testid={`venue-migration-${venueId}`}>
      <Box display="flex" flexDirection="column" gap={300} padding={300}>
        <Text variant="h3" as="h2">
          Venue {migration?.progress?.venueId ?? venueId}
        </Text>
        {isLoadingStatus && !migration && <Text variant="body">Checking migration status...</Text>}
        {statusError && (
          <Alert
            color="warning"
            title="Migration status unavailable"
            message={statusError.message}
          />
        )}
        {actionError && <Alert color="danger" title="Request failed" message={actionError} />}
        {migration && (
          <>
            <Alert {...presentMigration(migration)} />
            {migration.progress && (
              <Box display="flex" flexDirection="column" gap={100}>
                <Text variant="bodyStrong">{stageLabels[migration.progress.stage]}</Text>
                <Text variant="bodySm" color="fgSubtle">
                  Scheduled for {formatTimestamp(migration.progress.scheduledMigrationTime)} ·
                  migration attempt {migration.progress.migrationAttempt}
                </Text>
              </Box>
            )}
            {migration.progress?.issues.map((issue, index) => (
              <Alert
                key={`${issue.type}-${index}`}
                color="warning"
                title={issueLabels[issue.type]}
                message={issue.message}
              />
            ))}
            <Text variant="bodySm" color="fgSubtle">
              Temporal workflow {migration.workflowIdentifier.workflowId}
              {migration.workflowIdentifier.runId
                ? `, run ${migration.workflowIdentifier.runId}`
                : ''}
              .
            </Text>
            {(migration.canContinue || migration.canRetry) && (
              <Box display="flex" gap={200} flexWrap="wrap">
                {migration.canContinue && (
                  <Button
                    label={isContinuing ? 'Continuing...' : 'Confirm and continue'}
                    color="success"
                    disabled={isContinuing || isRetrying}
                    onClick={handleContinue}
                  />
                )}
                {migration.canRetry && (
                  <Button
                    label={isRetrying ? 'Retrying...' : 'Retry migration'}
                    variant="outlined"
                    disabled={isContinuing || isRetrying}
                    onClick={handleRetry}
                  />
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

const VenueMigrationRoute: React.FC = () => {
  const [venueIdsInput, setVenueIdsInput] = useState('');
  const [scheduledMigrationTime, setScheduledMigrationTime] = useState('');
  const [trackedVenueIds, setTrackedVenueIds] = useState<number[]>([]);
  const [statusRefreshKey, setStatusRefreshKey] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleVenueMigration] = useScheduleVenueMigrationMutation();

  const handleSchedule = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const parsedVenueIds = parseVenueIds(venueIdsInput);
    if (parsedVenueIds.venueIds === null) {
      setFormError(parsedVenueIds.error);
      return;
    }
    const migrationTime = new Date(scheduledMigrationTime);
    if (!scheduledMigrationTime || Number.isNaN(migrationTime.getTime())) {
      setFormError('Choose a valid migration date and time.');
      return;
    }
    if (migrationTime.getTime() <= Date.now()) {
      setFormError('Migration time must be in the future.');
      return;
    }

    setIsScheduling(true);
    const results = await Promise.allSettled(
      parsedVenueIds.venueIds.map((venueId) =>
        scheduleVenueMigration({
          variables: {
            venueId,
            scheduledMigrationTime: migrationTime.toISOString(),
          },
        })
      )
    );
    setIsScheduling(false);
    setTrackedVenueIds(parsedVenueIds.venueIds);
    setStatusRefreshKey((key) => key + 1);

    const failedVenueIds = parsedVenueIds.venueIds.filter(
      (_, index) => results[index].status === 'rejected'
    );
    if (failedVenueIds.length > 0) {
      setFormError(
        `Could not schedule ${failedVenueIds.length} venue${failedVenueIds.length === 1 ? '' : 's'}: ${failedVenueIds.join(', ')}. Their status cards will still attempt to find an existing workflow.`
      );
    }
  };

  const handleLookup = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const parsedVenueIds = parseVenueIds(venueIdsInput);
    if (parsedVenueIds.venueIds === null) {
      setFormError(parsedVenueIds.error);
      return;
    }
    setTrackedVenueIds(parsedVenueIds.venueIds);
    setStatusRefreshKey((key) => key + 1);
  };

  return (
    <Box padding={[0, 500, 500]} data-testid="venue-migration-page">
      <PageHeader
        title="Venue migrations"
        description="Schedule one durable migration workflow per venue, then monitor and resolve each venue independently. Times are entered in your local time zone and stored as UTC."
      />
      <Box display="flex" flexDirection="column" gap={400} padding={[300, 0]}>
        <Alert
          color="danger"
          title="Experimental — do not use in production"
          message="This proof of concept relies heavily on mock activities, mock migration results, and randomly generated issues. Use it only for local development and demonstrations."
        />
        <Card>
          <Box display="flex" flexDirection="column" gap={300} padding={300}>
            <Text variant="h3" as="h2">
              Schedule or find a migration
            </Text>
            <Textarea
              id="venue-migration-venue-ids"
              label="Venue ids"
              hint={`Enter comma-, space-, or newline-separated venue ids. Maximum ${MAX_VENUE_IDS}.`}
              value={venueIdsInput}
              onChange={(event) => setVenueIdsInput(event.target.value)}
              rows={5}
              fullWidth
            />
            <TextInput
              id="venue-migration-time"
              label="Scheduled migration time"
              type="datetime-local"
              value={scheduledMigrationTime}
              onChange={(event) => setScheduledMigrationTime(event.target.value)}
              fullWidth
            />
            <Box display="flex" gap={200} flexWrap="wrap">
              <Button
                label={isScheduling ? 'Scheduling...' : 'Schedule migrations'}
                type="button"
                disabled={isScheduling}
                onClick={handleSchedule}
              />
              <Button
                label="Find existing migrations"
                type="button"
                variant="outlined"
                disabled={isScheduling}
                onClick={handleLookup}
              />
            </Box>
          </Box>
        </Card>

        {formError && <Alert color="danger" title="Request failed" message={formError} />}
        {trackedVenueIds.map((trackedVenueId) => (
          <VenueMigrationCard
            key={`${statusRefreshKey}-${trackedVenueId}`}
            venueId={trackedVenueId}
          />
        ))}
      </Box>
    </Box>
  );
};

export default VenueMigrationRoute;
